<template>
  <m-pageframe title="{organization-label}: Lightbox Settings">
    <v-container>
      <m-beta-alert
        code="lightboxes"
        description="<b>Heads up!</b><br/><br/>Lightboxes are still new (i.e. in 'beta'). This feature allows you to create popup forms and messages for your website. We have done our best to test this feature out thoroughly, but it <i>might</i> still have a few bugs.  We are eager to get your feedback."
      />

      <card-grid-with-controls :show_loading_state="loading">
        <template #header>
          <h1 class="text-h4 font-weight-bold mb-1">Your Lightboxes</h1>
          <p class="text-body-2 text--secondary mb-0">
            Manage your lightbox popups and configure their display settings. Lightboxes can be used
            to create engaging popup forms and messages for your website visitors.
          </p>
        </template>

        <template #header-actions>
          <v-btn
            v-if="$rc.$state.isOrganizationAdmin()"
            color="primary"
            :disabled="loading"
            @click="onClickNew"
          >
            <v-icon class="mr-1">mdi-plus</v-icon>
            Create Lightbox
          </v-btn>
          <m-restricted-button
            v-else
            color="primary lighten-2"
            tooltip_text="Only organization admins can perform this action"
          >
            <v-icon class="mr-1">mdi-plus</v-icon>
            <span class="pr-2">
              Create Lightbox
            </span>
          </m-restricted-button>

          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                color="secondary"
                :disabled="loading"
                v-bind="attrs"
                v-on="on"
                outlined
                @click="onClickURLs"
              >
                <v-icon class="mr-1">mdi-link</v-icon>
                Edit URLs
              </v-btn>
            </template>
            <span>
              Configure where your lightboxes appear
            </span>
          </v-tooltip>

          <v-menu bottom offset-y left :disabled="loading">
            <template #activator="{ on }">
              <v-btn icon v-on="on">
                <v-icon aria-hidden="true">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list class="py-0" dense>
              <!-- <v-subheader>ANALYTICS & SUBMISSIONS</v-subheader> -->
              <v-list-item @click="viewLightboxSubmissions">
                <v-list-item-icon><v-icon>mdi-clipboard-list</v-icon></v-list-item-icon>
                <v-list-item-title>All Lightbox Submissions</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template #filter>
          <div class="d-flex align-center gap-3">
            <v-btn-toggle v-model="filter_status" dense mandatory>
              <v-btn value="all">All ({{ num_all }})</v-btn>
              <v-btn value="showing">Showing ({{ num_showing }})</v-btn>
              <v-btn value="not-showing">Not Showing ({{ num_not_showing }})</v-btn>
            </v-btn-toggle>
            <v-checkbox
              v-model="show_archived"
              label="Show Archived"
              :disabled="loading"
              hide-details
              class="mt-0 pt-0"
            />
          </div>
        </template>

        <template #search>
          <v-text-field
            v-show="!is_empty"
            dense
            hide-details
            outlined
            clearable
            label="Search"
            prepend-inner-icon="mdi-magnify"
            autocomplete="off"
            v-model="full_text"
          />
        </template>

        <template #grid>
          <v-col v-for="lightbox in results" :key="lightbox.lightbox_id" cols="12" md="6" lg="4">
            <card-with-action-header
              :header_text="lightbox.label"
              :is_archived="lightbox.archived"
              :menu_items="getMenuItems(lightbox)"
              :menu_aria_label="`Actions for ${lightbox.label} lightbox`"
              :aria_label="`Edit lightbox: ${lightbox.label}`"
              interactive
              @click="onClickEdit(lightbox)"
              @menu-action="onMenuAction"
            >
              <template v-if="!lightbox.archived">
                <div v-if="lightbox.comp_visibility !== 'never'" class="lightbox-content-section">
                  <div>
                    <div class="setting-item">
                      <v-icon size="16" color="success" class="mr-2">mdi-eye</v-icon>
                      <span class="setting-label">Shows On:</span>
                      <strong class="setting-value">
                        {{ getVisibilityText(lightbox.comp_visibility) }}
                      </strong>
                    </div>
                    <div class="setting-item">
                      <v-icon size="16" color="success" class="mr-2">
                        mdi-timer-outline
                      </v-icon>
                      <span class="setting-label">Display Delay:</span>
                      <strong class="setting-value">{{ getDisplayDelayText(lightbox) }}</strong>
                    </div>
                    <div class="setting-item">
                      <v-icon size="16" color="success" class="mr-2">
                        mdi-clock-outline
                      </v-icon>
                      <span class="setting-label">Show Once Per:</span>
                      <strong class="setting-value">
                        {{ getShowOncePerText(lightbox.hide_display_duration_ms) }}
                      </strong>
                    </div>
                  </div>
                </div>

                <div
                  v-if="lightbox.comp_visibility === 'never'"
                  class="lightbox-content-section setup-cta-section"
                >
                  <div class="d-flex flex-column gap-3">
                    <div>
                      <v-alert
                        class="shaded-light full-width mb-0"
                        border="left"
                        color="primary"
                        colored-border
                        dense
                      >
                        <v-icon color="primary" size="16" class="mr-2">mdi-eye-off</v-icon>
                        <span class="text-body-2 d-inline-block">
                          This lightbox won't show to visitors yet
                        </span>
                      </v-alert>
                    </div>

                    <p v-if="$rc.$state.isOrganizationAdmin()" class="mb-0">
                      Configure which URLs this lightbox should appear on to make it visible to your
                      website visitors.
                    </p>
                    <p class="mb-0" v-else>
                      To make this lightbox visible to your website visitors, please contact an
                      organization admin to configure its URL settings.
                    </p>

                    <div v-if="$rc.$state.isOrganizationAdmin()" class="d-flex flex-wrap gap-2">
                      <v-btn
                        color="primary"
                        elevation="0"
                        :disabled="!$rc.$state.isOrganizationAdmin()"
                        small
                        @click.stop="onClickShowAlways(lightbox)"
                      >
                        <v-icon size="16" left>mdi-eye</v-icon>
                        Show on Every Page
                      </v-btn>
                      <v-btn
                        color="secondary"
                        :disabled="!$rc.$state.isOrganizationAdmin()"
                        small
                        outlined
                        @click.stop="onClickURLs"
                      >
                        <v-icon small left>mdi-link</v-icon>
                        Edit URLs
                      </v-btn>
                    </div>
                  </div>
                </div>
              </template>

              <div v-else class="lightbox-content-section">
                <v-alert
                  class="shaded-light full-width mb-0"
                  border="left"
                  color="grey darken-1"
                  colored-border
                  dense
                >
                  <v-icon color="grey darken-1" size="16" class="mr-2">mdi-archive</v-icon>
                  <span class="text-body-2">
                    This lightbox is archived and will not be shown.
                  </span>
                </v-alert>
              </div>
            </card-with-action-header>
          </v-col>
        </template>
      </card-grid-with-controls>
    </v-container>
  </m-pageframe>
</template>

<script>
import _ from 'lodash';

export default {
  components: {
    CardGridWithControls: () => import('@/components/layouts/card-grid-with-controls.vue'),
    CardWithActionHeader: () => import('@/components/shared/cards/card-with-action-header.vue'),
  },

  data() {
    return {
      lightboxes: null,
      full_text: null,
      filter_status: 'all',
      show_archived: false,
      loading: false,
    };
  },

  mounted() {
    this.update();
  },

  methods: {
    update() {
      this.loadLightboxes();
    },

    loadLightboxes() {
      this.loading = true;

      // endpoint currently does not accept body due to being GET,
      // so filtering by status/search text is all client-side via `results` computed property
      this.$rc.rest({
        spinner: false,
        endpoint: 'GET:/api/v1/lightbox/organization/:organization_id',

        params: {
          organization_id: this.$route.params.organization_id,
        },

        on_success: data => {
          // sort lightboxes by archived status, then by visibility
          const order = {
            'all-pages': 0,
            'some-pages': 1,
            'specific-pages': 1,
            never: 2,
          };
          if (Array.isArray(data.settings)) {
            data.settings = _.cloneDeep(data.settings).sort((a, b) => {
              if (a.archived !== b.archived) {
                return a.archived ? 1 : -1;
              }
              const a_order =
                order[a.comp_visibility] !== undefined ? order[a.comp_visibility] : 99;
              const b_order =
                order[b.comp_visibility] !== undefined ? order[b.comp_visibility] : 99;
              return a_order - b_order;
            });
          }
          this.lightboxes = data;
          this.loading = false;
        },

        on_error: () => {
          this.loading = false;
          this.$rc.alert('Unable to list lightbox settings');
        },
      });
    },

    getVisibilityText(visibility) {
      const visibilityMap = {
        'all-pages': 'All Pages',
        never: 'Never',
        'specific-pages': 'Some Pages',
        'some-pages': 'Some Pages',
      };
      return visibilityMap[visibility] || visibility;
    },

    getDisplayDelayText(lightbox) {
      if (!lightbox.comp_display_delay_ms) return 'Immediate';
      const seconds = lightbox.comp_display_delay_ms / 1000;
      const type = lightbox.comp_display_delay_type || 'seconds';
      return `${seconds}s (${type})`;
    },

    getShowOncePerText(duration_ms) {
      if (!duration_ms) return '24 hours';
      const hours = Math.round(duration_ms / (60 * 60 * 1000));
      return `${hours} hours`;
    },

    getMenuItems(lightbox) {
      const menu_items = [];
      const should_disable_write = !this.$rc.$state.isOrganizationAdmin();

      menu_items.push({
        text: 'Edit',
        action: 'edit',
        icon: 'mdi-pencil',
        item: lightbox,
      });

      menu_items.push({ divider: true });

      menu_items.push({
        text: lightbox.archived ? 'Unarchive' : 'Archive',
        action: lightbox.archived ? 'unarchive' : 'archive',
        icon: lightbox.archived ? 'mdi-package-up' : 'mdi-archive',
        item: lightbox,
        org_admins_only: true,
      });

      if (!lightbox.archived) {
        menu_items.push({ divider: true });

        menu_items.push({
          text: 'Show on every page',
          action: 'show-always',
          icon: 'mdi-eye',
          item: lightbox,
          org_admins_only: true,
          disabled: should_disable_write || lightbox.comp_visibility === 'all-pages',
        });

        menu_items.push({
          text: 'Never show',
          action: 'show-never',
          icon: 'mdi-eye-off',
          item: lightbox,
          org_admins_only: true,
          disabled: should_disable_write || lightbox.comp_visibility === 'never',
        });

        menu_items.push({ divider: true });

        menu_items.push({
          text: 'Set uniform display delay',
          action: 'set-delay',
          icon: 'mdi-timer',
          item: lightbox,
          org_admins_only: true,
          disabled: should_disable_write || lightbox.comp_visibility === 'never',
        });
      }

      return menu_items;
    },

    onMenuAction(action, item) {
      const { item: lb } = item;

      switch (action) {
        case 'edit':
          this.onClickEdit(lb);
          break;
        case 'archive':
          this.onClickToggleArchived(lb);
          break;
        case 'unarchive':
          this.onClickToggleArchived(lb);
          break;
        case 'show-always':
          this.onClickShowAlways(lb);
          break;
        case 'show-never':
          this.onClickShowNever(lb);
          break;
        case 'set-delay':
          this.onClickSetUniformDelay(lb);
          break;
      }
    },

    onClickEdit(item) {
      this.$router.push({
        name: 'lightbox-edit',
        params: {
          organization_id: item.organization_id,
          lightbox_id: item.lightbox_id,
        },
      });
    },

    onClickNew() {
      this.$router.push({
        name: 'lightbox-template-selection',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    doCreateNew(label) {
      if (label == null) label = '';
      if (label.trim().length == 0) return;

      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/lightbox/settings/create-stub',

        params: {
          organization_id: this.$route.params.organization_id,
          label: label,
        },

        on_success: data => {
          this.onClickEdit(data);
        },

        on_error: 'Unable to create new lightbox',
      });
    },

    onClickToggleArchived(item) {
      let patch = {
        lightbox_id: item.lightbox_id,
        archived: !item.archived,
      };

      this.patch(patch);
    },

    patch(patch) {
      this.$rc.rest({
        spinner: true,
        endpoint: 'PATCH: /api/v1/lightbox',

        params: patch,

        on_success: data => {
          this.update();
        },

        on_error: 'Unable to patch lightbox settings',
      });
    },

    onClickShowAlways(item) {
      this.$rc.confirm({
        title: 'Show Lightbox on Every Page',
        body:
          'Show "' +
          item.label +
          '" on every page?  All other lightboxes will be set to never show',
        btn_text_ok: 'Show on every page',
        btn_text_cancel: 'Cancel',
        on_ok: () => {
          this.doClickShowAlways(item);
        },
      });
    },

    doClickShowAlways(item) {
      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/lightbox/urls/show-on-all-pages',

        params: {
          lightbox_id: item.lightbox_id,
        },

        on_success: data => {
          this.update();
        },

        on_error: 'Unable to show lightbox on every page',
      });
    },

    onClickShowNever(item) {
      this.$rc.confirm({
        title: 'Never Show Lightbox',
        body: 'Never show "' + item.label + '"?',
        btn_text_ok: 'Never Show',
        btn_text_cancel: 'Cancel',
        on_ok: () => {
          this.doClickShowNever(item);
        },
      });
    },

    doClickShowNever(item) {
      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/lightbox/urls/never-show',

        params: {
          lightbox_id: item.lightbox_id,
        },

        on_success: data => {
          this.update();
        },

        on_error: 'Unable to set lightbox to never show',
      });
    },

    onClickSetUniformDelay(item) {
      const initial_value = (item.comp_display_delay_ms ?? 0) / 1000;

      this.$rc.prompt({
        title: 'Set uniform display delay',
        label: 'Set a uniform delay (in seconds) before showing the lightbox. ',
        value: initial_value,
        unset_label: null,
        is_valid: this.$rc.validationRuleInteger(0, null),

        after_prompt: value => {
          this.doSetUniformDelay(item, value * 1000);
        },
      });
    },

    doSetUniformDelay(item, delay_ms) {
      this.$rc.rest({
        spinner: false,
        endpoint: 'PUT: /api/v1/lightbox/urls/set-uniform-display-delay',

        params: {
          lightbox_id: item.lightbox_id,
          delay: delay_ms,
        },

        on_success: data => {
          this.update();
        },

        on_error: 'Unable to set uniform lightbox display delay.',
      });
    },

    onClickURLs() {
      this.$router.push({
        name: 'lightbox-urls',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },

    viewLightboxSubmissions() {
      this.$router.push({
        name: 'lightbox-submissions',
        params: {
          organization_id: this.$route.params.organization_id,
        },
      });
    },
  },

  computed: {
    is_empty() {
      return !this.lightboxes || !this.lightboxes.settings || this.lightboxes.settings.length === 0;
    },

    results() {
      if (this.lightboxes == null) return [];

      let full_text = this.full_text;

      if (full_text == null) full_text = '';
      full_text = full_text.trim().toLowerCase();

      const all_settings = this.lightboxes.settings;

      const filter_full_text = item => {
        return item.label.toLowerCase().includes(full_text);
      };

      const filter_archived = item => {
        if (!this.show_archived && item.archived) {
          return false;
        }
        return true;
      };

      const filter_status = item => {
        if (this.filter_status === 'showing') {
          return item.comp_visibility !== 'never';
        } else if (this.filter_status === 'not-showing') {
          return item.comp_visibility === 'never';
        } else {
          return true;
        }
      };

      return all_settings
        .filter(filter_full_text)
        .filter(filter_archived)
        .filter(filter_status);
    },

    num_showing() {
      if (this.lightboxes == null || !this.lightboxes.settings) return 0;
      return this.lightboxes.settings.filter(item => {
        if (!this.show_archived && item.archived) return false;
        return item.comp_visibility !== 'never';
      }).length;
    },

    num_not_showing() {
      if (this.lightboxes == null || !this.lightboxes.settings) return 0;
      return this.lightboxes.settings.filter(item => {
        if (!this.show_archived && item.archived) return false;
        return item.comp_visibility === 'never';
      }).length;
    },

    num_archived() {
      if (this.lightboxes == null || !this.lightboxes.settings) return 0;
      return this.lightboxes.settings.filter(item => item.archived).length;
    },

    num_all() {
      if (this.lightboxes == null || !this.lightboxes.settings) return 0;
      return this.lightboxes.settings.filter(item => {
        if (!this.show_archived && item.archived) return false;
        return true;
      }).length;
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  max-width: 84rem;
}

.lightbox-status-indicators {
  display: flex;
  gap: 0.33rem;
  flex-wrap: wrap;
}

.lightbox-content-section {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
    padding-bottom: 1.25rem;
  }

  .section-header {
    display: flex;
    align-items: center;
    margin-top: 0.25rem;
    margin-bottom: 0.5rem;

    .section-icon {
      margin-right: 0.5rem;
    }

    .section-title {
      font-size: 0.875rem;
      font-weight: 600;
      margin: 0;
      color: rgba(0, 0, 0, 0.87);
    }
  }
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
  font-size: 0.875rem;

  .setting-label {
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
  }

  .setting-value {
    color: rgba(0, 0, 0, 0.87);
    text-align: right;
    margin-left: auto;
  }
}
</style>
